import "react-circular-progressbar/dist/styles.css";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import styled from "styled-components";

import { ArrowLeftColor, TitlePage } from "@components";
import { PATH_BMIHISTORY, PATH_HOME } from "@configs";
import { selectMerchant } from "@redux";
import { RenderMeta } from "@utils";
import { Radio } from "antd";
import { useState } from "react";
import { bmiApi } from "src/API/bmi";
import { useFullScreen } from "src/Helpers/useFullscreen";

// Goal mapping for display text
const goalTextMap: { [key: number]: string } = {
    0: "Weight Loss",
    1: "Protein",
    2: "Muscle",
    3: "Improve Endurance",
};

// Activity level mapping
const activityLevelMap: { [key: number]: "0" | "1-2" | "3-4" | "5-7" } = {
    0: "0",
    1: "1-2",
    2: "3-4",
    3: "5-7",
};

// Calculate BMI
const calculateBMI = (weight: number, height: number): number => {
    const heightInMeters = height / 100;
    return weight / (heightInMeters * heightInMeters);
};

// Get BMI status
const getBmiStatus = (bmi: number): string => {
    if (bmi < 18.5) return "Underweight";
    if (bmi < 25) return "Normal";
    if (bmi < 30) return "Overweight";
    return "Obese";
};

// Calculate nutrition values
const calculateNutrition = (
    weight: number,
    height: number,
    age: number,
    gender: "male" | "female",
    physicalActivityLevel: "0" | "1-2" | "3-4" | "5-7"
) => {
    const activityMultiplier: { [key in "0" | "1-2" | "3-4" | "5-7"]: number } = {
        "0": 1.2,
        "1-2": 1.375,
        "3-4": 1.55,
        "5-7": 1.725,
    };

    const bmr =
        gender === "male"
            ? 10 * weight + 6.25 * height - 5 * age + 5
            : 10 * weight + 6.25 * height - 5 * age - 161;

    const calories = Math.round(bmr * activityMultiplier[physicalActivityLevel]);
    const protein = Math.round((calories * 0.3) / 4);
    const fat = Math.round((calories * 0.25) / 9);
    const carbohydrates = Math.round((calories * 0.45) / 4);
    const saturatedFat = Math.round(fat * 0.3);
    const sugar = Math.round(carbohydrates * 0.2);

    return { calories, protein, fat, carbohydrates, saturatedFat, sugar };
};

export const ComponentBmiForm = () => {
    //page hook
    const { t } = useTranslation();
    //push route
    const history = useHistory();
    //redux statex

    const { themes } = useSelector(selectMerchant);
    const merchant = useSelector(selectMerchant);
    const [formData, setFormData] = useState({
        gender: "",
        age: "",
        weight: "",
        height: "",
        activityLevel: null as number | null,
        goal: null as number | null,
    });

    // const [surveyList, setSurveyList] = useState<ISurvey[]>([]);
    const fullHeight = useFullScreen();
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});

    const activities = [
        { id: 0, img: "../../images/day-0.svg", text: "Day a Week" },
        { id: 1, img: "../../images/day-1.svg", text: "Day a Week" },
        { id: 2, img: "../../images/day-2.svg", text: "Day a Week" },
        { id: 3, img: "../../images/day-3.svg", text: "Day a Week" },
    ];

    const selectgoals = [
        { id: 0, img: "../../images/goal-01.svg", text: "Weight Loss" },
        { id: 1, img: "../../images/goal-02.svg", text: "Protein" },
        { id: 2, img: "../../images/goal-03.svg", text: "Muscle" },
        { id: 3, img: "../../images/goal-04.svg", text: "Improve Endurance" },
    ];
    const handleChange = (field: string, value: any) => {
        if ((field === "weight" || field === "height") && value > 999) {
            return;
        } else if (field === "age" && value > 99) {
            return;
        }
        setFormData({
            ...formData,
            [field]: value,
        });
        // Clear error when user fills the field
        if (errors[field]) {
            setErrors({ ...errors, [field]: false });
        }
    };

    const handleSubmit = async () => {
        const newErrors: { [key: string]: boolean } = {};

        if (!formData.gender) newErrors.gender = true;
        if (!formData.age) newErrors.age = true;
        if (!formData.weight) newErrors.weight = true;
        if (!formData.height) newErrors.height = true;
        if (formData.activityLevel === null) newErrors.activityLevel = true;
        if (formData.goal === null) newErrors.goal = true;

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Parse values
        const weight = parseFloat(formData.weight);
        const height = parseFloat(formData.height);
        const age = parseInt(formData.age);
        const gender = formData.gender.toLowerCase() as "male" | "female";
        const activityLevel = activityLevelMap[formData.activityLevel!];
        const goalText = goalTextMap[formData.goal!];

        // Calculate BMI and nutrition
        const bmi = calculateBMI(weight, height);
        const bmiStatus = getBmiStatus(bmi);
        const nutrition = calculateNutrition(weight, height, age, gender, activityLevel);

        // Prepare data to send to API and pass to result page
        const calculatedData = {
            // Original form data
            gender: formData.gender,
            age,
            weight,
            height,
            activityLevel: formData.activityLevel,
            goal: formData.goal,
            // Calculated values
            bmi: parseFloat(bmi.toFixed(2)),
            bmiStatus,
            calories: nutrition.calories,
            protein: nutrition.protein,
            carbohydrates: nutrition.carbohydrates,
            fat: nutrition.fat,
            sugar: nutrition.sugar,
            saturatedFat: nutrition.saturatedFat,
            goalText,
        };
        try {
            await bmiApi.addBmi({
                age,
                height,
                weight,
                gender: formData.gender,
                bmi: calculatedData.bmi,
                bmiStatus,
                calories: nutrition.calories,
                protein: nutrition.protein,
                carbohydrates: nutrition.carbohydrates,
                fat: nutrition.fat,
                sugar: nutrition.sugar,
                saturatedFat: nutrition.saturatedFat,
                goal: goalText,
            });
            history.push({
                pathname: "/bmi-result",
                state: calculatedData,
            });
        } catch (error) {
            console.error("Failed to save BMI record:", error);
            history.push({
                pathname: "/bmi-result",
                state: calculatedData,
            });
        }
    };

    return (
        <>
            <RenderMeta title="BMI FORM" />
            <StyledBmiForm
                style={{ height: fullHeight }}
                themeColor={themes?.themeColor!}
                bgImg="../../images/bg-calculator.svg"
                contentColor={merchant?.luckyDrawDisplaySetting?.textColor!}
                themePageColor="#76C0D9"
            >
                <header>
                    <div className="back-arrow">
                        <ArrowLeftColor onClick={() => history.push(PATH_HOME)} color="white" />
                    </div>
                    <div className="header-title">
                        <TitlePage text="CALCULATOR" />
                    </div>
                    <div className="icon-history" onClick={() => history.push(PATH_BMIHISTORY)}>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M10.5 22H6.00003C4.34316 22 3.00001 20.6568 3.00003 19L3.00014 4.99998C3.00016 3.34313 4.3433 2 6.00014 2H16C17.6569 2 19 3.34315 19 5V10.5"
                                stroke={"white"}
                                strokeWidth="1.5"
                                strokeLinecap="round"
                            />
                            <path
                                d="M7 7H15M7 11H11"
                                stroke={"white"}
                                strokeWidth="1.5"
                                strokeLinecap="round"
                            />
                            <path
                                d="M18 18.5L16.5 17.95V15.5M12 17.5C12 19.9853 14.0147 22 16.5 22C18.9853 22 21 19.9853 21 17.5C21 15.0147 18.9853 13 16.5 13C14.0147 13 12 15.0147 12 17.5Z"
                                stroke={"white"}
                                strokeWidth="1.5"
                                strokeLinecap="round"
                            />
                        </svg>
                    </div>
                </header>

                <div className="wrapper-bmi-form">
                    <div className="title-sub-bmi-form">Please enter informtation</div>
                    <div className="form-group-bmi">
                        <div className="gender-bmi-form">
                            <div className="input-title">
                                Gender
                                {errors.gender && (
                                    <span className="error-text">*Please select gender</span>
                                )}
                            </div>
                            <div className="grid-gender-bmi">
                                <div
                                    className={`box-gender-bmi ${
                                        formData.gender === "Male" ? "selected" : ""
                                    }`}
                                    onClick={() => handleChange("gender", "Male")}
                                >
                                    <Radio checked={formData.gender === "Male"}>Male</Radio>
                                </div>
                                <div
                                    className={`box-gender-bmi ${
                                        formData.gender === "Female" ? "selected" : ""
                                    }`}
                                    onClick={() => handleChange("gender", "Female")}
                                >
                                    <Radio checked={formData.gender === "Female"}>Female</Radio>
                                </div>
                            </div>
                        </div>
                        <div className="input-form-detail">
                            <p className="input-title">
                                Age
                                {errors.age && <span className="error-text">*Please fill age</span>}
                            </p>
                            <input
                                className="form-input"
                                type="tel"
                                inputMode="numeric"
                                step="1"
                                value={formData.age}
                                onChange={(e) => handleChange("age", e.target.value)}
                                onKeyDown={(e) => e.key === "." && e.preventDefault()}
                            />
                            <span>Years old</span>
                        </div>
                        <div className="grid-bmi-form">
                            <div className="input-form-detail">
                                <p className="input-title">
                                    Weight
                                    {errors.weight && (
                                        <span className="error-text">*Please fill weight</span>
                                    )}
                                </p>
                                <input
                                    className="form-input"
                                    type="number"
                                    value={formData.weight}
                                    onChange={(e) => handleChange("weight", e.target.value)}
                                />
                                <span>KG</span>
                            </div>
                            <div className="input-form-detail">
                                <p className="input-title">
                                    Height
                                    {errors.height && (
                                        <span className="error-text">*Please fill height</span>
                                    )}
                                </p>
                                <input
                                    className="form-input"
                                    type="number"
                                    value={formData.height}
                                    onChange={(e) => handleChange("height", e.target.value)}
                                />
                                <span>CM</span>
                            </div>
                        </div>
                        <div className="title-form-bmi">
                            Physical Activity Level*
                            {errors.activityLevel && (
                                <span className="error-text">*Please select</span>
                            )}
                        </div>
                        <div className="list-bmi-form">
                            {activities.map((activity, index) => (
                                <div
                                    key={activity.id}
                                    className={`box-bmi-form ${
                                        formData.activityLevel === activity.id ? "selected" : ""
                                    }`}
                                    onClick={() => handleChange("activityLevel", activity.id)}
                                >
                                    <div className="img-bmi-form">
                                        <img src={activity.img} alt={activity.text} />
                                    </div>
                                    <div className="text-bmi-form">{activity.text}</div>
                                    {formData.activityLevel === activity.id && (
                                        <span>
                                            <img
                                                src="../../images/ic-select-bmi.svg"
                                                alt="Selected"
                                            />
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="form-group-bmi">
                        <div className="title-form-bmi">
                            Select your goal*
                            {errors.goal && <span className="error-text">*Please select</span>}
                        </div>
                        <div className="list-bmi-form">
                            {selectgoals.map((goal, index) => (
                                <div
                                    key={goal.id}
                                    className={`box-bmi-form ${
                                        formData.goal === goal.id ? "selected" : ""
                                    }`}
                                    onClick={() => handleChange("goal", goal.id)}
                                >
                                    <div className="img-bmi-form">
                                        <img src={goal.img} alt={goal.text} />
                                    </div>
                                    <div className="text-bmi-form">{goal.text}</div>
                                    {formData.goal === goal.id && (
                                        <span>
                                            <img
                                                src="../../images/ic-select-bmi.svg"
                                                alt="Selected"
                                            />
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="layout-btn-form">
                        <div className="box-btn-form">
                            <StyledReset>
                                <img src="../../images/ic-reset.svg" alt="" />
                            </StyledReset>
                        </div>
                        <div className="box-btn-form">
                            <StyledBtnBmiForm
                                themeColor={themes?.themeColor!}
                                onClick={handleSubmit}
                            >
                                Submit Calculation
                            </StyledBtnBmiForm>
                        </div>
                    </div>
                </div>
            </StyledBmiForm>
        </>
    );
};

const StyledReset = styled.button`
    background: #e8e8e8;
    width: 100%;
    font-size: 13px;
    border: none;
    font-weight: 600;
    border-radius: 16px;
    height: 45px;
    display: flex;
    align-items: center;
    justify-content: center;
    img {
        width: 24px;
        height: 24px;
    }
`;

const StyledBtnBmiForm = styled.button<{ themeColor: string }>`
    color: #fff;
    background: ${(p) => p.themeColor};
    width: 100%;
    font-size: 13px;
    border: none;
    font-weight: 600;
    border-radius: 16px;
    height: 45px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const StyledBmiForm = styled.div<{
    themeColor: string;
    bgImg: string;
    contentColor: string;
    themePageColor: string;
}>`
    width: 100%;
    margin: 0 auto;
    /* height: 200vh;
    min-height: 200vh; */
    overflow: scroll;
    background-color: #76c0d9;
    background-position: center top;
    background-repeat: no-repeat;
    background-image: url("../../images/bg-calculator.svg");
    background-size: 100%;
    position: relative;
    .logo-survey {
        font-size: 32px;
        color: ${(p) => p.themeColor};
        font-weight: 600;
    }
    .back-arrow {
        display: flex;
        align-items: center;
        cursor: pointer;
        color: white;
    }
    .header-title {
        flex: 1;
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .icon-history {
        display: flex;
        align-items: center;
        cursor: pointer;
    }
    header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px;
        min-height: 15vh;
        .title-group {
            text-align: center;
        }
        h4 {
            flex: 1;
            display: flex;
            justify-content: center;
            font-style: normal;
            font-weight: 600;
            font-size: 16px;
            line-height: 25px;
            color: #000000;
        }
    }
    .wrapper-bmi-form {
        background-color: #fff;
        padding: 30px 0;
        border-top-right-radius: 16px;
        border-top-left-radius: 16px;
        .title-sub-bmi-form {
            font-size: 16px;
            line-height: 19px;
            color: #000000;
            font-weight: 600;
            margin-bottom: 30px;
            padding: 0 16px;
        }
        .form-group-bmi {
            padding: 0 16px;
            .title-form-bmi {
                font-size: 14px;
                line-height: 20px;
                color: #9b9b9b;
                margin-bottom: 8px;
            }
            .error-text {
                color: #e74c3c;
                font-size: 12px;
                margin-left: 8px;
                font-weight: 400;
            }
            .list-bmi-form {
                display: grid;
                grid-template-columns: 1fr 1fr 1fr 1fr;
                gap: 16px;
                padding: 12px 8px;
                .box-bmi-form {
                    text-align: center;
                    cursor: pointer;
                    position: relative;
                    .img-bmi-form {
                        width: 64px;
                        height: 64px;
                        margin: 0 auto 8px;
                        img {
                            width: 100%;
                            height: 100%;
                            object-fit: cover;
                            filter: grayscale(100%);
                            transition: filter 0.3s;
                        }
                    }
                    .text-bmi-form {
                        font-size: 12px;
                        line-height: 15px;
                        color: #000000;
                    }
                    span {
                        position: absolute;
                        top: -5px;
                        right: 10px;
                    }
                    &.selected {
                        .img-bmi-form img {
                            filter: grayscale(0%);
                        }
                    }
                }
            }
            .grid-bmi-form {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 16px;
            }
            .gender-bmi-form {
                margin-bottom: 15px;
                .input-title {
                    font-size: 14px;
                    line-height: 17px;
                    color: #000;
                    margin-bottom: 15px;
                }
                .grid-gender-bmi {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    .box-gender-bmi {
                    }
                }
            }
            .input-form-detail {
                margin-bottom: 30px;
                position: relative;
                span {
                    position: absolute;
                    right: 16px;
                    top: calc(50% + 10px);
                    transform: translateY(-50%);
                    font-size: 14px;
                    line-height: 17px;
                    color: #000;
                }
                .input-title {
                    font-size: 14px;
                    line-height: 17px;
                    color: #000;
                    margin-bottom: 5px;
                    .error-text {
                        position: static;
                        transform: none;
                        color: #e74c3c;
                        font-size: 12px;
                        margin-left: 8px;
                        font-weight: 400;
                    }
                }
                .form-input {
                    box-shadow: none !important;
                    height: 65px;
                    font-size: 14px;
                    color: #9b9b9b;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    border-radius: 16px;
                    width: 100%;
                    border: 1px solid #9b9b9b;
                    cursor: default;
                    padding-right: 100px !important;
                    padding-left: 16px;
                    /* Hide number input arrows across browsers */
                    appearance: textfield;
                    -moz-appearance: textfield;
                    &::-webkit-outer-spin-button,
                    &::-webkit-inner-spin-button {
                        margin: 0;
                        -webkit-appearance: none;
                    }
                }
            }
        }
        .layout-btn-form {
            display: grid;
            grid-template-columns: 50px 1fr;
            margin-top: 24px;
            padding: 0 16px;
            gap: 16px;
        }
    }
`;
